/**
 * Project dashboard "What's new": counts of issue and document changes in
 * the timeframe the viewer picked, derived in render from the raw rows the
 * repository fetched for that timeframe.
 *
 * The cache holds raw rows rather than counts so the window can roll forward
 * on a ticking clock without a refetch, and so classifying a status as done
 * reads the CURRENT status catalogue (a custom status gaining category "done"
 * re-sorts the counts immediately).
 */

/** The timeframes the viewer can pick, narrowest first. */
export const RECENT_CHANGE_WINDOWS = [
  { key: "day", hours: 24 },
  { key: "threeDays", hours: 72 },
  { key: "week", hours: 24 * 7 },
  { key: "month", hours: 24 * 30 },
] as const;

export type RecentChangeWindow = (typeof RECENT_CHANGE_WINDOWS)[number]["key"];

export const DEFAULT_RECENT_CHANGE_WINDOW: RecentChangeWindow = "threeDays";

const HOUR_MS = 60 * 60 * 1000;

export function windowMs(window: RecentChangeWindow): number {
  const found = RECENT_CHANGE_WINDOWS.find((w) => w.key === window);
  return (found ?? RECENT_CHANGE_WINDOWS[1]).hours * HOUR_MS;
}

export function isRecentChangeWindow(value: unknown): value is RecentChangeWindow {
  return RECENT_CHANGE_WINDOWS.some((w) => w.key === value);
}

/** One activity row, projected to what the summary and "For you" read. */
export interface RecentIssueActivityRow {
  id: number;
  issue_id: number;
  action_type: string;
  actor_id: number | null;
  created_at: string;
  old_value: unknown;
  new_value: unknown;
  /** Display snapshots written by the trigger (`actor_name`, `discipline`, …). */
  metadata: Record<string, unknown>;
  issue_number: number;
  /** The viewer-language display title (document root issues show the filename). */
  issue_title: string;
  /** The issue's CURRENT status. */
  issue_status: string;
}

/** One document row, projected to what the summary and its examples read. */
export interface RecentDocumentRow {
  id: number;
  created_at: string;
  version: number | null;
  filename: string;
  /** What the shared document viewer needs to open the row. */
  file_path: string | null;
  content_type: string | null;
  size: number | null;
  state: string | null;
}

/**
 * Rows newest-first, as the repository returns them. `truncated` means the
 * fetch stopped at its row cap before reaching the timeframe's start, so the
 * counts are lower bounds.
 */
export interface RecentRows<T> {
  rows: T[];
  truncated: boolean;
}

/** A count, and whether it is a lower bound ("at least") rather than exact. */
export interface WindowCount {
  count: number;
  atLeast: boolean;
}

export interface IssueChangeSummary {
  /** Issues created. */
  opened: WindowCount;
  /** Distinct issues moved from an open status to a done one. */
  completed: WindowCount;
  /** Distinct issues with any change at all, new ones included. */
  updated: WindowCount;
  /** Comments posted. */
  comments: WindowCount;
}

export interface DocumentChangeSummary {
  /** Documents that started a new lineage (version 1). */
  added: WindowCount;
  /** Later versions added to an existing document. */
  newVersions: WindowCount;
}

/** Answers "does this status key mean finished work in this project?" */
export type IsDoneStatus = (statusKey: string) => boolean;

/**
 * Rows inside the timeframe ending at `now`. No upper bound: `now` is a
 * once-a-minute clock, so a row realtime just delivered is routinely newer
 * than it — and it still happened inside the timeframe.
 */
export function rowsInWindow<T extends { created_at: string }>(
  rows: T[],
  now: number,
  window: RecentChangeWindow
): T[] {
  const start = now - windowMs(window);
  return rows.filter((row) => Date.parse(row.created_at) >= start);
}

/**
 * Count the distinct keys `keyOf` yields for the rows in the timeframe
 * (`null` = the row does not count). For plain counts the key is the row id.
 */
function countInWindow<T extends { created_at: string }>(
  source: RecentRows<T>,
  now: number,
  window: RecentChangeWindow,
  keyOf: (row: T) => string | number | null
): WindowCount {
  const seen = new Set<string | number>();
  for (const row of rowsInWindow(source.rows, now, window)) {
    const k = keyOf(row);
    if (k != null) seen.add(k);
  }
  return { count: seen.size, atLeast: source.truncated };
}

export type IssueMetric = keyof IssueChangeSummary;

/**
 * Whether an activity row counts toward `metric`, and as what: the key it is
 * counted under (rows sharing a key count once), or null. The counts and the
 * example lists both go through this, so they cannot disagree.
 */
function completedIssueKey(row: RecentIssueActivityRow, isDone: IsDoneStatus): number | null {
  return row.action_type === "status_changed" &&
    typeof row.old_value === "string" &&
    typeof row.new_value === "string" &&
    !isDone(row.old_value) &&
    isDone(row.new_value)
    ? row.issue_id
    : null;
}

export function issueMetricKey(
  metric: IssueMetric,
  row: RecentIssueActivityRow,
  isDone: IsDoneStatus
): string | number | null {
  switch (metric) {
    case "opened":
      return row.action_type === "issue_created" ? row.id : null;
    case "completed":
      // Open → done only: done → done (resolved → closed) is not a completion,
      // and keying by issue keeps a reopen-and-close-again to one.
      return completedIssueKey(row, isDone);
    case "updated":
      return row.issue_id;
    case "comments":
      return row.action_type === "comment_added" ? row.id : null;
  }
}

export const ISSUE_METRICS: IssueMetric[] = ["opened", "completed", "updated", "comments"];

export function summariseIssueActivity(
  source: RecentRows<RecentIssueActivityRow>,
  now: number,
  window: RecentChangeWindow,
  isDone: IsDoneStatus
): IssueChangeSummary {
  const count = (metric: IssueMetric) =>
    countInWindow(source, now, window, (r) => issueMetricKey(metric, r, isDone));
  return {
    opened: count("opened"),
    completed: count("completed"),
    updated: count("updated"),
    comments: count("comments"),
  };
}

/** Version 1 (or an unversioned row) starts a lineage; anything later extends one. */
export function isNewLineage(row: RecentDocumentRow): boolean {
  return row.version == null || row.version <= 1;
}

export function summariseDocumentRows(
  source: RecentRows<RecentDocumentRow>,
  now: number,
  window: RecentChangeWindow
): DocumentChangeSummary {
  return {
    added: countInWindow(source, now, window, (r) => (isNewLineage(r) ? r.id : null)),
    newVersions: countInWindow(source, now, window, (r) => (isNewLineage(r) ? null : r.id)),
  };
}
